import { useState } from "react";
import { UyariKurallari } from "./UyariKurallari";
import { useQuery } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  bosListeMetni,
  Buton,
  cn,
  FiltreSeridi,
  Iskelet,
  Secim,
  Sekmeler,
  type EtkinFiltre,
} from "@koclukpanel/ui";
import { olayMetni } from "@koclukpanel/contracts";
import {
  ApiHatasi,
  logGorunumu,
  logSorgula,
  type LogSatiri,
  type OlayKategorisi,
  type OlaySiddeti,
} from "./api";

/**
 * DENETIM LOG EKRANI — F-LOG, matris §5.17
 *
 * ================================================================
 * FILTRE LISTESI SUNUCUDAN
 * ================================================================
 * Ekran on bir kategoriyi sabit yazmiyor: `/denetim/log/gorunum`
 * kullanicinin GORDUGU kategorileri donuyor ve filtreler ondan
 * uretiliyor.
 *
 * Sabit yazsaydik iki dogruluk kaynagi olurdu — ekran bir kategoriyi
 * gosterir, sunucu bos doner ve kullanici "guvenlik olayi yok" diye
 * dusunurdu. Oysa dogrusu "senin gorme yetkin yok".
 */

const KATEGORI_ADI: Record<OlayKategorisi, string> = {
  IDENTITY: "Kimlik",
  SESSION: "Oturum",
  DATA_CHANGE: "Veri değişikliği",
  DATA_READ: "Okuma",
  AUTHORIZATION: "Yetki",
  WORKFLOW: "İş akışı",
  COMMUNICATION: "İletişim",
  FINANCE: "Finans",
  EXPORT: "Dışa aktarma",
  SYSTEM: "Sistem",
  SECURITY: "Güvenlik",
};

const SIDDET_ADI: Record<OlaySiddeti, string> = {
  INFO: "Bilgi",
  NOTICE: "Dikkat",
  WARNING: "Uyarı",
  CRITICAL: "Kritik",
};

const SIDDET_SINIFI: Record<OlaySiddeti, string> = {
  INFO: "bg-notr-100 text-notr-600",
  NOTICE: "bg-marka-50 text-marka-700",
  WARNING: "bg-uyari-500/15 text-uyari-800",
  CRITICAL: "bg-hata-500/15 text-hata-700",
};

function zaman(iso: string): string {
  return new Date(iso).toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

interface Filtre {
  kategoriler: OlayKategorisi[];
  tur: string;
  aktor: string;
  ogrenci: string;
  modul: string;
  siddet: string;
  baslangic: string;
  bitis: string;
}

const BOS_FILTRE: Filtre = {
  kategoriler: [],
  tur: "",
  aktor: "",
  ogrenci: "",
  modul: "",
  siddet: "",
  baslangic: "",
  bitis: "",
};

/** Metin alanlarinin kullaniciya gorunen adlari — cip etiketleri buradan. */
const ALAN_ADI: Record<Exclude<keyof Filtre, "kategoriler">, string> = {
  tur: "Tür",
  aktor: "Aktör",
  ogrenci: "Öğrenci",
  modul: "Modül",
  siddet: "Şiddet",
  baslangic: "Başlangıç",
  bitis: "Bitiş",
};

/**
 * UYGULANAN filtreden çip listesi üretir — G-UYG-10.
 *
 * ================================================================
 * TASLAK DEĞİL, UYGULANAN
 * ================================================================
 * Ekranda iki filtre durumu var: kullanıcının yazdığı (`filtre`) ve
 * "Uygula"ya basınca listeyi süzen (`uygulanan`). Şerit UYGULANANI
 * gösteriyor — taslağı gösterseydi, henüz uygulanmamış bir alanı
 * "etkin" diye yazar ve listenin neden değişmediği anlaşılmazdı.
 *
 * Saf fonksiyon: bileşen olmadan test edilebiliyor.
 */
export function etkinFiltreler(
  uygulanan: Filtre,
  kaldir: (alan: keyof Filtre, deger?: string) => void,
): EtkinFiltre[] {
  const cikti: EtkinFiltre[] = [];

  /*
    HER KATEGORI AYRI CIP. "Kategori: 3 seçili" yazsaydik kullanici
    hangilerini sectigini gormek icin paneli yeniden acmak zorunda
    kalirdi — ve tek tek kaldiramazdi.
  */
  for (const k of uygulanan.kategoriler) {
    cikti.push({
      anahtar: `kategori-${k}`,
      etiket: `Kategori: ${k}`,
      onKaldir: () => kaldir("kategoriler", k),
    });
  }

  for (const alan of Object.keys(ALAN_ADI) as Array<keyof typeof ALAN_ADI>) {
    const deger = uygulanan[alan];
    if (deger) {
      cikti.push({
        anahtar: alan,
        etiket: `${ALAN_ADI[alan]}: ${deger}`,
        onKaldir: () => kaldir(alan),
      });
    }
  }

  return cikti;
}

export function Loglar() {
  /**
   * Uyari kurallari AYRI BIR MENU DEGIL, buranin sekmesi.
   *
   * "Hangi olaylar izleniyor" sorusu, olay listesine bakan kisinin
   * sorusu. Ayri menu acsaydik, log ekranini acan biri kurallarin
   * varligini bile bilmeyebilirdi.
   */
  const [sekme, setSekme] = useState<"olaylar" | "kurallar">("olaylar");
  const [filtre, setFiltre] = useState<Filtre>(BOS_FILTRE);
  const [uygulanan, setUygulanan] = useState<Filtre>(BOS_FILTRE);
  const [sayfalar, setSayfalar] = useState<string[]>([]);

  const gorunum = useQuery({ queryKey: ["log-gorunum"], queryFn: logGorunumu });

  const imlec = sayfalar.at(-1);
  const sorgu = useQuery({
    queryKey: ["loglar", uygulanan, imlec ?? null],
    queryFn: () =>
      logSorgula({
        kategori: uygulanan.kategoriler.join(",") || undefined,
        tur: uygulanan.tur || undefined,
        aktor: uygulanan.aktor || undefined,
        ogrenci: uygulanan.ogrenci || undefined,
        modul: uygulanan.modul || undefined,
        siddet: uygulanan.siddet || undefined,
        baslangic: uygulanan.baslangic || undefined,
        bitis: uygulanan.bitis || undefined,
        imlec,
      }),
    /*
      Kategori YOKSA sorgu HIC atilmiyor. `gorunum.isSuccess` tek
      basina yetmiyordu: yetkisiz kullanicida ekran "yetkiniz yok"
      diyor ama arkada gereksiz bir istek gidiyordu — cevabi zaten
      atilan bir istek.
    */
    enabled: gorunum.isSuccess && (gorunum.data?.kategoriler.length ?? 0) > 0,
  });

  if (gorunum.isPending) return <Iskelet satir={4} />;

  if (gorunum.isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Log görünümü alınamadı"
        aciklama={gorunum.error instanceof ApiHatasi ? gorunum.error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void gorunum.refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  /*
    HICBIR KATEGORI GORMUYORSA bunu ACIKCA soylemek gerekiyor.
    Bos bir liste gostermek "log yok" gibi okunur; oysa log var,
    kullanicinin yetkisi yok.
  */
  if (gorunum.data.kategoriler.length === 0) {
    return (
      <BosDurum
        baslik="Log görüntüleme yetkiniz yok"
        aciklama="Hiçbir olay kategorisine erişiminiz bulunmuyor. Bu, kayıt olmadığı anlamına gelmez."
      />
    );
  }

  const kategoriDegistir = (k: OlayKategorisi) => {
    setFiltre((f) => ({
      ...f,
      kategoriler: f.kategoriler.includes(k)
        ? f.kategoriler.filter((x) => x !== k)
        : [...f.kategoriler, k],
    }));
  };

  const uygula = () => {
    setUygulanan(filtre);
    setSayfalar([]); // Filtre degisince sayfalama BASA doner.
  };

  const temizle = () => {
    setFiltre(BOS_FILTRE);
    setUygulanan(BOS_FILTRE);
    setSayfalar([]);
  };

  /*
    TEK FILTRE KALDIRMA "temizle"den FARKLI bir is. Dort filtre kurup
    besincinin fazla oldugunu anlayan kullanici, otekileri bastan
    kurmak zorunda kalmamali.

    IKI DURUM DA guncelleniyor (`filtre` taslak, `uygulanan` yururlukte):
    yalnizca `uygulanan`i temizleseydik panel hala eski degeri gosterir
    ve kullanici "kaldirdim ama duruyor" derdi.
  */
  const filtreKaldir = (alan: keyof Filtre, deger?: string) => {
    const guncelle = (f: Filtre): Filtre =>
      alan === "kategoriler"
        ? { ...f, kategoriler: f.kategoriler.filter((k) => k !== deger) }
        : { ...f, [alan]: "" };
    setFiltre(guncelle);
    setUygulanan(guncelle);
    setSayfalar([]);
  };

  const etkin = etkinFiltreler(uygulanan, filtreKaldir);

  return (
    <div className="flex flex-col gap-4">
      {/*
        Elle yazilmis `role="tab"` seridi kaldirildi: `aria-selected`
        koyuyordu ama ok tuslariyla gezinme, gezinen odak ve `tabpanel`
        bagi YOKTU — ekran okuyucu kullanicisina tutulmayan bir soz.
        `Sekmeler` gercekte olani anlatiyor (`aria-pressed`).
      */}
      <Sekmeler
        etiket="Log ekranı bölümleri"
        secili={sekme}
        onSec={setSekme}
        sekmeler={[
          { deger: "olaylar", etiket: "Olaylar" },
          { deger: "kurallar", etiket: "Uyarı kuralları" },
        ]}
      />

      {sekme === "kurallar" && <UyariKurallari />}

      {sekme === "olaylar" && (
        <>
          {/*
        MASKELEME UYARISI EN USTTE.
        Kullanici "degerler neden [maskeli] yaziyor" diye sormadan
        once cevabi gormeli — yoksa sistemi bozuk sanar.
      */}
          {gorunum.data.maskelemeVar && (
            <p className="rounded-alan bg-uyari-500/10 text-notr-800 p-3 text-sm">
              Veri değişikliği olaylarında <strong>alan adları görünür, değerler gizlidir</strong>.
              Hangi alanın değiştiğini görürsünüz, ne olduğunu görmezsiniz — teknik yönetim rolünün
              veriye kör olması için bilinçli bir kısıt.
            </p>
          )}

          <FiltrePaneli
            filtre={filtre}
            setFiltre={setFiltre}
            kategoriler={gorunum.data.kategoriler.map((k) => k.kategori)}
            onKategori={kategoriDegistir}
            onUygula={uygula}
            onTemizle={temizle}
          />

          {/*
        SERIT PANELIN ALTINDA: panel "ne arayacagim", serit "su an ne
        ariyorum" sorusunu cevapliyor. Serit ustte olsaydi, henuz
        filtre kurmamis kullanici bos bir seride bakardi.
      */}
          <FiltreSeridi filtreler={etkin} onTemizle={temizle} />

          {sorgu.isPending && <p className="text-notr-600 text-sm">Aranıyor…</p>}

          {sorgu.isError && (
            <p role="alert" className="text-hata-500 text-sm">
              {sorgu.error instanceof ApiHatasi ? sorgu.error.message : "Log alınamadı."}
            </p>
          )}

          {sorgu.data && (
            <>
              {sorgu.data.satirlar.length === 0 ? (
                /*
              ================================================================
              FILTRESIZ BOS LISTE "BU FILTRELERLE" DEMIYOR — 04.09.2026
              ================================================================
              Onceden baslik SABITTI: hic filtre yokken de "Bu filtrelerle
              kayit yok" yaziyordu. Kullanici olmayan bir filtreyi aramaya
              gonderilmis oluyordu. Metin artik `bosListeMetni`den geliyor
              ve etkin filtre sayisina gore degisiyor (G-UYG-10).
            */
                <BosDurum
                  baslik={bosListeMetni(etkin.length).baslik}
                  aciklama={
                    uygulanan.kategoriler.length > 0
                      ? "Seçtiğiniz kategorilerden bazılarını görme yetkiniz olmayabilir; o kategoriler aramaya dâhil edilmez."
                      : bosListeMetni(etkin.length).aciklama
                  }
                />
              ) : (
                <ul className="flex flex-col gap-2">
                  {sorgu.data.satirlar.map((s) => (
                    <li key={s.id}>
                      <LogKarti satir={s} />
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex items-center gap-2">
                <Buton
                  gorunum="ikincil"
                  disabled={sayfalar.length === 0}
                  onClick={() => setSayfalar((s) => s.slice(0, -1))}
                >
                  ← Önceki
                </Buton>
                <Buton
                  gorunum="ikincil"
                  disabled={!sorgu.data.sonrakiImlec}
                  onClick={() => setSayfalar((s) => [...s, sorgu.data.sonrakiImlec as string])}
                >
                  Sonraki →
                </Buton>
                <span className="text-notr-600 text-xs">
                  {sayfalar.length + 1}. sayfa · {sorgu.data.satirlar.length} kayıt
                </span>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------

function FiltrePaneli({
  filtre,
  setFiltre,
  kategoriler,
  onKategori,
  onUygula,
  onTemizle,
}: {
  filtre: Filtre;
  setFiltre: (f: (o: Filtre) => Filtre) => void;
  kategoriler: OlayKategorisi[];
  onKategori: (k: OlayKategorisi) => void;
  onUygula: () => void;
  onTemizle: () => void;
}) {
  return (
    <form
      className="rounded-kart border-notr-200 bg-notr-0 shadow-kart flex flex-col gap-4 border p-4"
      onSubmit={(e) => {
        e.preventDefault();
        onUygula();
      }}
    >
      <fieldset className="flex flex-col gap-2">
        <legend className="text-notr-900 text-sm font-semibold">Kategori</legend>
        {/*
          Yalnizca GORULEBILEN kategoriler ciziliyor. Gorulemeyen bir
          kategoriyi gri gostermek de bir secenekti ama sistemde ne
          oldugunu sizdirirdi: "Finans olaylari var ama goremiyorsun"
          bilgisi bile bilgidir.
        */}
        <div className="flex flex-wrap gap-2">
          {kategoriler.map((k) => {
            const secili = filtre.kategoriler.includes(k);
            return (
              <button
                key={k}
                type="button"
                onClick={() => onKategori(k)}
                aria-pressed={secili}
                className={cn(
                  "min-h-dokunma rounded-alan px-3 text-sm font-medium",
                  "focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                  secili
                    ? "bg-marka-600 text-notr-0"
                    : "bg-notr-100 text-notr-700 hover:bg-notr-200",
                )}
              >
                {KATEGORI_ADI[k]}
              </button>
            );
          })}
        </div>
        <p className="text-notr-600 text-xs">
          Hiçbiri seçilmezse görebildiğiniz tüm kategoriler aranır.
        </p>
      </fieldset>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Alan
          etiket="Olay türü"
          value={filtre.tur}
          onChange={(e) => setFiltre((f) => ({ ...f, tur: e.target.value }))}
          placeholder="LOGIN_SUCCESS"
        />
        <Alan
          etiket="Aktör"
          yardim="Ad ya da soyad yazın."
          value={filtre.aktor}
          onChange={(e) => setFiltre((f) => ({ ...f, aktor: e.target.value }))}
        />
        <Alan
          etiket="Öğrenci"
          yardim="Ad, soyad ya da öğrenci numarası."
          value={filtre.ogrenci}
          onChange={(e) => setFiltre((f) => ({ ...f, ogrenci: e.target.value }))}
        />
        <Alan
          etiket="Modül"
          value={filtre.modul}
          onChange={(e) => setFiltre((f) => ({ ...f, modul: e.target.value }))}
          placeholder="kisi"
        />

        {/*
          HAM `<select>` KALDIRILDI: alanin alti gorsel hali
          `alan-bicimi.ts` icinde tek yerde tanimli ve elle yazilan her
          alan o sozlesmenin disinda kaliyordu — odak halkasi burada
          `focus:ring` ile ciziliyordu, oysa panelin geri kalani
          `:focus-visible` kullaniyor (fareyle tiklayana halka
          cikmiyor, klavyeyle gelene cikiyor).
        */}
        <Secim
          etiket="Şiddet"
          value={filtre.siddet}
          onChange={(e) => setFiltre((f) => ({ ...f, siddet: e.target.value }))}
        >
          <option value="">Hepsi</option>
          {(Object.keys(SIDDET_ADI) as OlaySiddeti[]).map((s) => (
            <option key={s} value={s}>
              {SIDDET_ADI[s]}
            </option>
          ))}
        </Secim>

        <Alan
          etiket="Başlangıç"
          type="datetime-local"
          value={filtre.baslangic}
          onChange={(e) => setFiltre((f) => ({ ...f, baslangic: e.target.value }))}
        />
        <Alan
          etiket="Bitiş"
          type="datetime-local"
          value={filtre.bitis}
          onChange={(e) => setFiltre((f) => ({ ...f, bitis: e.target.value }))}
        />
      </div>

      <div className="flex gap-2">
        <Buton type="submit">Ara</Buton>
        <Buton gorunum="sessiz" type="button" onClick={onTemizle}>
          Temizle
        </Buton>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------

export function LogKarti({ satir }: { satir: LogSatiri }) {
  const [acik, setAcik] = useState(false);

  return (
    <article className="rounded-kart border-notr-200 bg-notr-0 shadow-kart border p-3">
      <div className="flex flex-wrap items-start gap-3">
        <span className="text-notr-600 shrink-0 text-xs tabular-nums">
          {zaman(satir.occurredAt)}
        </span>

        <span className="min-w-0 flex-1">
          {/*
            OKUNABILIR CUMLE EN USTTE, TEKNIK KOD ALTINDA.

            Log ekranina bakan kisi cogu zaman gelistirici degil:
            `IMPERSONATION_STARTED` ona "kim ne yapti" sorusunu
            cevaplamiyor. Cumle aktorun hemen ustunde duruyor ki ikisi
            birlikte okunsun.

            Teknik kod KALDIRILMIYOR, kuculuyor: bir olayin izini
            surmek ya da filtre kutusuna tam tipi yazmak gerektiginde
            aranan sey o. Iki gosterim birbirinin yerine gecmiyor.

            Tanimsiz bir tipte cumle HIC cizilmiyor — bos bir satir
            yerine bugunku gorunume dusuyor (bkz. `olayMetni`).
          */}
          {olayMetni(satir.type) && (
            <span className="text-notr-900 block text-sm font-medium">{olayMetni(satir.type)}</span>
          )}
          <span className="text-notr-600 block text-xs">
            {KATEGORI_ADI[satir.category]} · <span className="font-mono">{satir.type}</span>
          </span>
          <span className="text-notr-600 mt-0.5 block text-xs">
            {/*
              SISTEM olaylarinda aktor YOK ve bunu "bilinmiyor" diye
              gostermek yanlis olurdu: sistemin kendisi yapti.
            */}
            {/*
              ================================================================
              AD ÖNCE, ROL SONRA, KİMLİK HİÇ — 07.09.2026
              ================================================================
              Burada `actorRole ?? actorId` vardı: rol bilinmiyorsa
              satırda ham kimlik (`cms5q65d300hxv7302f00q0zb`)
              görünüyordu ve denetim kaydına bakan kişi o kimliğin kim
              olduğunu hiçbir yerden öğrenemiyordu.
            */}
            {satir.isSystem
              ? "Sistem"
              : satir.actorAdi
                ? `${satir.actorAdi}${satir.actorRole ? ` · ${satir.actorRole}` : ""}`
                : (satir.actorRole ?? "—")}
            {satir.module ? ` · ${satir.module}` : ""}
            {satir.entityType ? ` · ${satir.entityType}` : ""}
            {satir.outcome !== "SUCCESS" ? ` · ${satir.outcome}` : ""}
          </span>
        </span>

        <span
          className={cn(
            "rounded-alan shrink-0 px-2.5 py-0.5 text-xs font-medium",
            SIDDET_SINIFI[satir.severity],
          )}
        >
          {SIDDET_ADI[satir.severity]}
        </span>

        {satir.changes && (
          <Buton gorunum="ikincil" onClick={() => setAcik((a) => !a)} aria-expanded={acik}>
            {acik ? "Gizle" : `${satir.changes.length} alan`}
          </Buton>
        )}
      </div>

      {satir.reason && (
        <p className="text-notr-600 mt-2 text-xs">
          <strong className="font-medium">Gerekçe:</strong> {satir.reason}
        </p>
      )}

      {acik && satir.changes && (
        <div className="border-notr-200 mt-3 border-t pt-3">
          {/*
            MASKELI oldugu SATIR BAZINDA yaziyor. Ustteki genel uyari
            yetmez: kullanici tek bir kaydi acip "burada deger neden
            yok" diye sorabilir.
          */}
          {satir.maskeliMi && (
            <p className="text-uyari-600 mb-2 text-xs">
              Değerler yetkiniz gereği gizlendi; alan adları görünür.
            </p>
          )}
          <table className="w-full text-sm">
            <caption className="sr-only">Değişen alanlar</caption>
            <thead>
              <tr className="text-notr-600 text-left text-xs">
                <th scope="col" className="pb-1 font-medium">
                  Alan
                </th>
                <th scope="col" className="pb-1 font-medium">
                  Önce
                </th>
                <th scope="col" className="pb-1 font-medium">
                  Sonra
                </th>
              </tr>
            </thead>
            <tbody>
              {satir.changes.map((d) => (
                <tr key={d.field} className="border-notr-100 border-t">
                  <td className="text-notr-700 py-1 font-mono text-xs">{d.field}</td>
                  <td className="text-notr-600 py-1">{bicimle(d.oldValue)}</td>
                  <td className="text-notr-900 py-1">{bicimle(d.newValue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </article>
  );
}

/**
 * Deger bicimi.
 *
 * `undefined` ile `null` AYRI gosteriliyor: birincisi "alan yoktu",
 * ikincisi "alan vardi ve bostu". Denetimde bu fark onemli.
 */
function bicimle(deger: unknown): string {
  if (deger === undefined) return "—";
  if (deger === null) return "(boş)";
  if (typeof deger === "string") return deger;
  return JSON.stringify(deger);
}
